<template>
  <v-row>
    <v-col cols="12" sm="8">
      <v-card>
        <template v-if="video">
          <video controls style="width:100%">
            <source :src="video">
            {{ $t('Your browser does not support the video tag.') }}
          </video>
        </template>
        <v-img v-else-if="image" class="white--text align-end" :src="image" />
        <v-card-title>
          <h3>{{ page.title }}</h3>
        </v-card-title>

        <v-card-text>
          <div v-html="markdownToHtml(page.text)"></div>
          <template v-if="attachments.length">
            <div class="">
              <v-list>
                <template v-for="attachment in attachments">
                  <v-list-item :key="`attachment-${attachment.id}`" @click="openAttachment(attachment)">
                    <v-list-item-icon>
                      <v-icon>{{ icons.mdiAttachment }}</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      {{ attachment.name }}
                    </v-list-item-content>
                  </v-list-item>
                  <v-divider :key="attachment.id"></v-divider>
                </template>
              </v-list>
            </div>
          </template>

          <!-- Acknowledgment Section -->
          <template v-if="page.require_acknowledgment && tenant.settings.acknowledgments_enabled">
            <v-card outlined class="mt-4">
              <v-card-title class="pb-2">
                <v-icon class="mr-2">{{ icons.mdiDraw }}</v-icon>
                {{ $t('Lukukuittaus') }}
              </v-card-title>
              <v-card-text class="pt-3">
                <!-- Status messages -->
                <v-alert v-if="acknowledgmentStatus === 'current'" type="success" text class="mb-3">
                  {{ $t('Olet kuitannut tämän luetuksi') }} {{ acknowledgmentDate ? acknowledgmentDate : '' }}
                </v-alert>

                <v-alert v-else-if="acknowledgmentStatus === 'outdated'" type="warning" text class="mb-3">
                  {{ $t('Sisältö on päivittynyt viime kuittauksesi jälkeen. Kuittaa uudelleen.') }}
                </v-alert>

                <v-alert v-else-if="acknowledgmentStatus === 'none'" type="info" text class="mb-3">
                  {{ $t('Tämä sisältö vaatii lukukuittauksen.') }}
                </v-alert>

                <!-- Acknowledgment description if available -->
                <div v-if="page.acknowledgment_description && acknowledgmentStatus !== 'current'" class="mb-3">
                  <div>{{ page.acknowledgment_description }}</div>
                </div>

                <!-- Acknowledgment button -->
                <v-btn v-if="acknowledgmentStatus !== 'current'" color="primary" :loading="isSubmittingAcknowledgment"
                  @click="submitAcknowledgment" class="mt-2">
                  {{ $t('Kuittaa luetuksi') }}
                </v-btn>
              </v-card-text>
            </v-card>
          </template>
        </v-card-text>
      </v-card>
    </v-col>
    <v-col sm="4">
      <v-card>
        <v-card-text>
          <v-treeview open-all :items="pages" item-key="id" item-text="title" item-children="children" ref="treeview">
            <template v-slot:label="{ item }">
              <div class="d-flex justify-space-between companypage-list-item">
                <router-link
                  :to="{ name: 'company-page', params: { id: item.id }, query: { category: route.query.category } }">
                  <b v-if="item.id == id"> {{ item.title }} </b>
                  <template v-else> {{ item.title }} </template>
                </router-link>
                <v-icon v-if="item.require_acknowledgment && tenant.settings.acknowledgments_enabled"
                  :color="item.read ? 'success' : 'error'" class="ml-3" size="20" :title="$t('Lukukuittaus vaaditaan')">
                  {{ icons.mdiDraw }}
                </v-icon>
              </div>
            </template>
          </v-treeview>
        </v-card-text>
      </v-card>
    </v-col>
    <v-dialog v-model="isDownloading" hide-overlay persistent width="240">
      <v-card color="primary" dark>
        <v-card-text class="pt-2">
          {{ $t("Ladataan...") }}

          <v-progress-linear indeterminate color="white" class="mb-0 mt-2"></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import {
  computed,
  onMounted,
  ref,
  watch,
  nextTick,
} from "@vue/composition-api";
import { useTranslations } from "@/@core/libs/i18n";
import AcknowledgmentTypes from "@/@core/utils/acknowledgment_types";
import api from "@/api";
import useAuth from "@/useAuth";
import { getDateTime } from "@/@core/utils/datetime_helpers";
import useMarkdown from "@/@core/utils/useMarkdown";
import newslist from "@/components/news/newslist.vue";
import { mdiAttachment, mdiCheckCircle, mdiCheck, mdiAlertCircle, mdiDraw } from "@mdi/js";
import download from "downloadjs";
import { useRouter } from "@/@core/utils";


export default {
  props: ["id"],
  setup(props) {
    const { route } = useRouter();


    const pages = ref([]);
    const page = ref({});

    const { t } = useTranslations();
    const { tenant } = useAuth();
    const { markdownToHtml } = useMarkdown();
    const treeview = ref(null);
    const open = ref([]);
    const isDownloading = ref(false);

    // Acknowledgment state
    const acknowledgmentStatus = ref(null); // null, 'current', 'outdated', 'none'
    const isSubmittingAcknowledgment = ref(false);
    const acknowledgmentDate = ref(null);

    const fetchPage = async (id) => {
      const response = await api.getPage(id);

      if (response.ok) {
        page.value = response.data;

        // Check acknowledgment status if page requires acknowledgment
        if (page.value.require_acknowledgment && tenant.value.settings.acknowledgments_enabled) {
          await checkAcknowledgmentStatus(id);
        }
      } else {
        alert(response.error);
      }
    };

    const fetchPages = async () => {
      const response = await api.getPages(route.value.query.category);

      if (response.ok) {
        pages.value = response.data;

        nextTick(() => {
          treeview.value.updateAll(true);
        });
      } else {
        // alert(response.error);
      }
    };

    const attachments = computed(() => {
      if (page.value.attachments) {
        return page.value.attachments.filter((a) => a.attachment_type == 1);
      }
      return [];
    });

    const video = computed(() => {
      if (page.value.attachments) {
        const videoAttachment = page.value.attachments.find(
          (a) => a.attachment_type == 2
        );
        if (videoAttachment && videoAttachment.url) {
          return videoAttachment.url;
        }
      }
      return null;
    });

    const image = computed(() => {
      if (page.value.image) {
        if (Object.keys(page.value.image).length) {
          return page.value.image.full_hd;
        }
      }
      return null;
    });

    const openAttachment = async (attachment) => {
      isDownloading.value = true;
      const response = await api.downloadPageAttachment(
        page.value.id,
        attachment.id
      );
      isDownloading.value = false;

      if (response.ok) {
        download(response.data, attachment.name);
      } else {
        alert(response.error);
      }
    };

    // Acknowledgment methods
    const checkAcknowledgmentStatus = async (pageId) => {
      try {
        const response = await api.getUserAcknowledgments(AcknowledgmentTypes.Page, pageId); // target_type=1 for pages

        if (response.ok) {
          const acknowledgments = response.data;

          if (!acknowledgments || acknowledgments.length === 0) {
            acknowledgmentStatus.value = 'none';
            acknowledgmentDate.value = null;
            return;
          }

          // Compare acknowledgment version with current page version
          const currentVersion = page.value.acknowledgment_version;
          const latestAcknowledgment = acknowledgments.find(a => a.version == currentVersion);

          if (latestAcknowledgment) {
            acknowledgmentStatus.value = 'current';
            acknowledgmentDate.value = getDateTime(new Date(latestAcknowledgment.acknowledged_at));
          } else {
            acknowledgmentStatus.value = 'outdated';
            acknowledgmentDate.value = null;
          }
        }
      } catch (error) {
        console.error('Error checking acknowledgment status:', error);
        acknowledgmentStatus.value = 'none';
        acknowledgmentDate.value = null;
      }
    };

    const submitAcknowledgment = async () => {
      if (!page.value.id) return;

      isSubmittingAcknowledgment.value = true;

      try {
        const currentVersion = page.value.acknowledgment_version || 1;
        const response = await api.submitAcknowledgment(1, page.value.id, currentVersion); // target_type=1 for pages

        if (response.ok) {
          acknowledgmentStatus.value = 'current';
          acknowledgmentDate.value = getDateTime(new Date());
          // You could add a snackbar or toast notification here for success
        }
      } catch (error) {
        console.error('Error submitting acknowledgment:', error);
        alert(t('Failed to submit acknowledgment. Please try again.'));
      } finally {
        isSubmittingAcknowledgment.value = false;
      }
    };

    watch(
      () => props.id,
      (newId, prevId) => {
        page.value = {};

        fetchPage(newId);
      }
    );

    onMounted(() => {
      if (props.id) {
        fetchPage(props.id);
      }

      fetchPages();
    });

    return {
      page,
      pages,
      attachments,
      video,
      image,
      openAttachment,
      tenant,
      markdownToHtml,
      isDownloading,
      t,
      treeview,
      open,
      acknowledgmentStatus,
      isSubmittingAcknowledgment,
      acknowledgmentDate,
      checkAcknowledgmentStatus,
      submitAcknowledgment,
      route,
      icons: {
        mdiAttachment,
        mdiCheckCircle,
        mdiCheck,
        mdiAlertCircle,
        mdiDraw,
      },
    };
  },
};
</script>

<style scoped>
.companypage-list-item {
  padding: 4px 10px;
  border-bottom: 1px solid transparent;
}

.companypage-list-item:hover {
  border-bottom: 1px dashed rgb(146, 146, 146);
}

.v-treeview-node__prepend a,
.v-treeview-node__children a {
  text-decoration: none;
}
</style>
